/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useRef, useState } from 'react';
import { Button, Input, Modal, ModalBody, ModalContent, ModalFooter, ModalHeader, Pagination, Spinner, Table, TableBody, TableCell, TableColumn, TableHeader, TableRow, Tooltip } from '@nextui-org/react';
import { IconEdit, IconPlus, IconSearch, IconTrash } from '@tabler/icons-react';
import { useSelector } from 'react-redux';
import toast from 'react-hot-toast';
import moment from 'moment';
import { Constants } from '../../../utils';
import { IconSchoolHat } from '../../../assets/icons';
import { SettingsService } from '../../../services';
import ModalCreate from './create-school-year';

const MODAL_ACTION = {
  NONE: 0,
  CREATE: 1,
  VIEW_LAPSES: 2,
  EDIT: 3,
  DELETE: 4,
}

const Settings = () => {
  const [selectedItem, setSelectedItem] = useState();
  const [modalAction, setModalAction] = useState(MODAL_ACTION.NONE);
  const { canResetFilter, data, filterBy, goToPage, isLoading, pagination, reload, deleteItem } = useFetchTable();

  const onSelectItem = (item, action) => {
    setSelectedItem(item);
    setModalAction(action);
  }

  const closeModal = () => {
    setSelectedItem(null);
    setModalAction(MODAL_ACTION.NONE);
  }

  return (
    <div>
      {isLoading && (
        <div className="w-screen h-screen fixed inset-0 z-[70] flex justify-center items-center bg-white/30">
          <Spinner />
        </div>
      )}

      <Modal
        size="md"
        isOpen={modalAction !== MODAL_ACTION.NONE}
        onClose={closeModal}
        backdrop="blur"
        scrollBehavior="outside"
      >
        <ModalContent>
          {(onClose) => {
            if (modalAction === MODAL_ACTION.VIEW_LAPSES) return (
              <ModalViewLapses item={selectedItem} />
            )
            if ([MODAL_ACTION.CREATE, MODAL_ACTION.EDIT].includes(modalAction)) return (
              <ModalCreate
                item={selectedItem}
                onClose={onClose}
                onSuccess={() => {
                  closeModal();
                  reload();
                }}
              />
            )
            if (modalAction === MODAL_ACTION.DELETE) return (
              <ModalDelete
                item={selectedItem}
                onClose={onClose}
                onDelete={() => {
                  deleteItem(selectedItem?.id);
                  closeModal();
                }}
              />
            )
          }}
        </ModalContent>
      </Modal>

      <div className="flex items-center mb-6">
        <img className="w-6 h-6 object-contain" src={IconSchoolHat} alt="" />
        <p className="mb-0 text-md font-semibold ml-4">PERÍODOS ESCOLARES</p>
      </div>

      <Filters
        canResetFilter={canResetFilter}
        filterBy={filterBy}
        resetFilter={() => reload()}
      />

      <Table
        aria-label="Años Escolares"
        color="primary"
        topContentPlacement="inside"
        topContent={
          <div className="flex flex-row justify-between items-center gap-4">
            <h3 className="text-xl font-medium">Años Escolares</h3>
            <Button
              className="pr-2 bg-green font-medium text-white"
              endContent={<IconPlus color="white" />}
              onClick={() => onSelectItem(null, MODAL_ACTION.CREATE)}
            >
              Nuevo
            </Button>
          </div>
        }
      >
        <TableHeader>
          <TableColumn>Año Escolar</TableColumn>
          <TableColumn>Fecha de inicio</TableColumn>
          <TableColumn>Fecha de finalización</TableColumn>
          <TableColumn align="end" />
        </TableHeader>
        <TableBody items={data} emptyContent="No hay resultados">
          {(item) => {
            return (
              <TableRow key={item.id}>
                <TableCell>({item?.name})</TableCell>
                <TableCell>{moment(item?.init_date).format('DD-MM-YYYY')}</TableCell>
                <TableCell>{moment(item?.end_date).format('DD-MM-YYYY')}</TableCell>
                <TableCell align="right">
                  <div className="relative flex justify-end items-center gap-2">
                    <Tooltip content="Editar">
                      <IconEdit onClick={() => onSelectItem(item, MODAL_ACTION.EDIT)} />
                    </Tooltip>
                    <Tooltip content="Eliminar" color="danger">
                      <IconTrash className="text-danger" onClick={() => onSelectItem(item, MODAL_ACTION.DELETE)} />
                    </Tooltip>
                  </div>
                </TableCell>
              </TableRow>
            )
          }}
        </TableBody>
      </Table>

      <div className="flex w-full justify-center mt-4">
        <Pagination
          showControls
          variant="bordered"
          page={pagination.page}
          total={pagination.pages}
          onChange={goToPage}
        />
      </div>
    </div>
  );
}

const Filters = ({ canResetFilter, filterBy, resetFilter }) => {
  const initialFilter = {
    search: '',
    since: '',
    until: '',
  };
  const [form, setForm] = useState(initialFilter);

  const onChange = (value, target) => {
    setForm(s => ({ ...s, [target]: value }));
    filterBy(value, target);
  }

  return (
    <section className="mb-4 grid sm:flex grid-cols-2 lg:flex-row items-end gap-4">
      <Input
        classNames={{
          base: 'w-full sm:max-w-[15rem]',
          inputWrapper: 'border-1 h-10',
        }}
        label="Buscar"
        labelPlacement="outside"
        placeholder="Año Escolar"
        startContent={<IconSearch />}
        variant="bordered"
        radius="sm"
        value={form.search}
        onValueChange={v => onChange(v, 'search')}
      />
      <Input
        type="date"
        classNames={{
          base: 'w-full sm:max-w-[10rem]',
          inputWrapper: 'border-1 h-10',
          input: `pr-0 text-${!!form.since ? '[]' : 'foreground-400'}`,
        }}
        label="Desde"
        labelPlacement="outside"
        placeholder=" "
        variant="bordered"
        radius="sm"
        value={form.since}
        onValueChange={v => onChange(v, 'since')}
      />
      <Input
        type="date"
        classNames={{
          base: 'w-full md:max-w-[10rem]',
          inputWrapper: 'border-1 h-10',
          input: `pr-0 text-${!!form.until ? '[]' : 'foreground-400'}`,
        }}
        label="Hasta"
        labelPlacement="outside"
        placeholder=" "
        variant="bordered"
        radius="sm"
        value={form.until}
        onValueChange={v => onChange(v, 'until')}
      />

      {canResetFilter && (
        <Button
          variant="light"
          className="text-primaryDark"
          onClick={() => {
            setForm(initialFilter);
            resetFilter();
          }}
        >
          Limpiar filtros
        </Button>
      )}
    </section>
  )
}

const ModalViewLapses = ({ item }) => {
  return (
    <>
      <ModalHeader className="flex flex-col gap-1">Lapso ({item.name})</ModalHeader>
      <ModalBody className="p-0 pb-4">
        <Table aria-label="Lapsos" removeWrapper isStriped classNames={{ th: '!rounded-none' }}>
          <TableHeader>
            <TableColumn>Nombre</TableColumn>
            <TableColumn>Inicio</TableColumn>
            <TableColumn>Finalización</TableColumn>
          </TableHeader>
          <TableBody items={item.lapses ?? []} emptyContent="No hay lapsos">
            {(lapse) => (
              <TableRow key={lapse.id}>
                <TableCell>{lapse.name}</TableCell>
                <TableCell>{moment(lapse.start).format('DD-MM-YYYY')}</TableCell>
                <TableCell>{moment(lapse.end).format('DD-MM-YYYY')}</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </ModalBody>
    </>
  )
}

const ModalDelete = ({ item, onClose, onDelete }) => {
  return (
    <>
      <ModalHeader className="flex flex-col gap-1">Confirmación</ModalHeader>
      <ModalBody>
        <p className="text-center">¿Estás seguro de eliminar este año escolar?</p>
        <p className="text-center font-medium">{item.name}</p>
        <p className="text-center text-sm">Esta acción <span className="font-semibold text-danger"> es irreversible y eliminará todos los registros relacionados</span> al año escolar</p>
      </ModalBody>
      <ModalFooter className="justify-evenly">
        <Button variant="light" onPress={onClose}>Cancelar</Button>
        <Button color="danger" onPress={onDelete}>Aceptar</Button>
      </ModalFooter>
    </>
  )
}

const useFetchTable = () => {
  const initialFilters = {
    page: 1,
    perPage: Constants.PER_PAGE,
    search: '',
    since: null,
    until: null,
  };

  const initialPagination = {
    page: 1,
    pages: 1,
    total: 0,
    perPage: Constants.PER_PAGE,
  };

  const user = useSelector(state => state.user);
  const [data, setData] = useState([]);
  const [canFetch, setCanFetch] = useState(true);
  const [filters, setFilters] = useState(initialFilters);
  const [pagination, setPagination] = useState(initialPagination);

  const debounceTime = 500;
  const debounce = useRef();

  const fetchData = async () => {
    if (!canFetch) return;
    setCanFetch(false);

    try {
      filters.school_id = user?.id;
      const response = await SettingsService.school.getSchoolYears(filters);
      const { data, ...rest } = response;

      setData(data);
      setPagination(rest);
      setCanFetch(true);

    } catch (error) {
      setData([]);
      onError(String(error));
    }
  }

  const deleteItem = async (id) => {
    if (!canFetch) return;
    setCanFetch(false);

    try {
      await SettingsService.school.destroySchoolYear({ shool_id: user?.id, id });
      toast.success('Año escolar eliminado con éxito');
      fetchData();

    } catch (error) {
      onError(String(error));
    }
    setCanFetch(true);
  }

  const updateItem = async (item) => {
    if (!canFetch) return;
    setCanFetch(false);

    try {
      await SettingsService.school.updateSchoolYear({ shool_id: user?.id, ...item });
      toast.success('Año escolar actualizado con éxito');
      fetchData();

    } catch (error) {
      onError(String(error));
    }
    setCanFetch(true);
  }

  const getCurrentPagination = () => {
    // Truco para obtener el estado actualizado (pagination es mantenida con el estado actual por el componente Pagination)
    let pag;
    setPagination(s => {
      pag = s;
      return s;
    });
    return pag;
  }

  const canResetFilter = () => {
    const { page, perPage, ...initial } = initialFilters;
    const { page: _, perPage: __, ...current } = filters;
    const initFilter = JSON.stringify(initial);
    const currFilter = JSON.stringify(current);
    return initFilter !== currFilter;
  }

  const onError = (msg) => toast.error(msg);

  const reload = (inSamePage = false) => {
    setCanFetch(true);
    if (!inSamePage) setFilters(initialFilters);
    else fetchData();
  }

  const goToPage = (page) => {
    const pagination = getCurrentPagination();
    if (page >= 1 && page <= pagination.pages && page !== pagination.page) {
      setCanFetch(true);
      setFilters({ ...filters, page });
    }
  }

  const changePerPage = (perPage) => {
    setCanFetch(true);
    setFilters({ ...filters, perPage });
  }

  const filterBy = (value, target) => {
    if (debounce.current) clearTimeout(debounce.current);
    debounce.current = setTimeout(() => {
      setCanFetch(true);
      setFilters({ ...filters, page: 1, [target]: value });
    }, debounceTime);
  }

  useEffect(() => {
    fetchData();
  }, [filters]);

  return {
    canResetFilter: canResetFilter(),
    changePerPage,
    data,
    deleteItem,
    filters,
    filterBy,
    goToPage,
    isLoading: !canFetch,
    pagination,
    reload,
    updateItem,
  }
}

export default Settings;
